import React from 'react';
import {Assignment, AutoAwesome, CloudUpload, CompareArrows, Dashboard, FactCheck, FileDownload, Groups, History, MenuBook, Notifications, Science, Search, Speed, Storage, Timeline, VerifiedUser} from "@mui/icons-material";

export const featureConfigs = [{
    slug: 'ingredient-library',
    id: 'ingredient-library',
    icon: <MenuBook/>,
    overline: 'Ingredient intelligence',
    title: 'Centralise ingredient knowledge',
    description: 'Search, compare and manage ingredient information from internal records, public references and imported supplier data.',
    heroPlaceholder: 'Ingredient search and comparison workspace',
    image: '/assets/ingredient_library_v5.png',

    benefits: [{
        title: 'Find ingredients faster', description: 'Search one structured library instead of looking through scattered spreadsheets, supplier PDFs and internal files.',
    }, {
        title: 'Review ingredient context', description: 'Access technical properties, allergens, nutritional values, supplier information and regulatory notes from a single ingredient record.',
    }, {
        title: 'Reuse trusted data', description: 'Use consistent ingredient information across formulation, labelling and product development workflows.',
    },],

    howItWorks: [{
        title: 'Search the library', visual: 'ingredient-search-library', description: 'Use search and filters to find ingredients by name, category, supplier, allergen status or technical attributes.',
    }, {
        title: 'Open the ingredient record', visual: 'ingredient-record-review', description: 'Review nutritional data, technical properties, allergens, supplier documents and linked source information.',
    }, {
        title: 'Reuse in formulations', visual: 'ingredient-reuse-formulations', description: 'Use validated ingredient records when building new formulations or updating existing product formulation versions.',
    }],

    subfeatures: [{
        title: 'Ingredient search',
        visual: 'ingredient-search',
        description: 'Search and filter ingredients using structured fields, annotation labels and external identifiers across the full ingredient library.',
        bullets: ['Origin, type and physical-state filters', 'Taste, odor and technical-effect labels', 'Regulatory and external identifier filters', 'Keyword search across ingredient metadata',],
    }, {
        title: 'Ingredient records',
        visual: 'ingredient-records',
        description: 'Review each ingredient through a structured record with details, base properties, labelling information, production fields and linked usage data.',
        bullets: ['Ingredient details and base properties', 'Labelling and production fields', 'Usage and formulation links',],
    // }, {
    //     title: 'Source data and identifiers',
    //     visual: 'ingredient-source-identifiers',
    //     description: 'Track where ingredient information comes from and manage external identifiers such as INSA, FDB, ChEBI, E-numbers and other reference codes.',
    //     bullets: ['Original data source tracking', 'External database identifiers', 'Document and reference links',],
    }, {
        title: 'Nutrition and technical data',
        visual: 'ingredient-nutrition-technical',
        description: 'Review nutritional composition, contents, aminograms and physicochemical properties from the ingredient record.',
        bullets: ['Nutrition and contents data', 'Aminogram information', 'Physicochemical properties',],
    }, {
        title: 'Ingredient comparison',
        visual: 'ingredient-alternatives-comparison',
        description: 'Review potential alternatives by comparing ingredient records side by side across contents, nutrition, allergens, technical properties and annotations.',
        bullets: ['Alternative ingredient comparison', 'Contents, nutrition and technical data differences', 'Allergen, annotation and validation comparison',],
    }],

    related: ['formulation-designer', 'data-integration', 'development-assistant'],
}, {
    slug: 'formulation-designer',
    id: 'formulation-designer',
    icon: <Science/>,
    overline: 'Formulation designer',
    title: 'Build and validate formulations',
    description: 'Create formulations from ingredient data, adjust quantities and understand the impact on composition, cost, labelling and compliance.',
    heroPlaceholder: 'Formulation composition table and label preview',
    image: '/assets/formulation_designer_v1.png',
    benefits: [{
        title: 'Build from library', description: 'Create new recipes using pre-validated ingredients from your central library.'
    }, {
        title: 'Real-time adjustments', description: 'Instantly see how quantity changes affect the entire formulation output.'
    }, {title: 'Preview outputs', description: 'Generate labels and cost breakdowns before committing to validation.'}],
    capabilities: [{
        icon: <Assignment/>, title: 'Composition tables', description: 'Dynamic tables for managing ingredients and their proportions.'
    }, {
        icon: <Speed/>, title: 'Quantity management', description: 'Easy unit conversion and mass-balance calculations.'
    }, {
        icon: <Timeline/>, title: 'Cost support', description: 'Real-time cost estimation based on ingredient prices.'
    }, {
        icon: <FactCheck/>, title: 'Label preview', description: 'Automatic generation of ingredient lists and label drafts.'
    }, {
        icon: <VerifiedUser/>, title: 'Nutrition & Claims', description: 'Calculate nutritional profiles and check against target claims.'
    }, {
        icon: <AutoAwesome/>, title: 'Allergen checks', description: 'Automated allergen rollup from ingredients to final product.'
    }],
    howItWorks: [{
        title: 'Select and add', visual: 'fd-select-add', description: 'Select a product project and add ingredients from the library.'
    }, {
        title: 'Adjust and review', visual: 'fd-adjust-review', description: 'Fine-tune quantities and monitor real-time calculations.'
    }, {
        title: 'Preview outputs', visual: 'fd-generate-previews', description: 'Produce drafts for labelling, costing, and final validation.'
    }],
    subfeatures: [{
        title: 'Formula composition',
        visual: 'formula-composition',
        description: 'Build and edit formulations using ingredients from internal libraries or public reference sources.',
        bullets: ['Ingredient selection', 'Quantity and proportion editing']
    }, {
        title: 'Smart labelling',
        visual: 'smart-labelling',
        description: 'Prepare EU-oriented labelling outputs from formulation data, including ingredient lists, allergens and label versions.',
        bullets: ['Ingredient list generation', 'Allergen declaration support']
    }, {
        title: 'Nutrition and claims',
        visual: 'nutrition-claims',
        description: 'Generate nutritional tables from formulation data, compare real and inferred values, evaluate nutrition-claim eligibility and estimate Nutrition score as quantities change.',
        bullets: ['Nutritional table generation', 'Real and inferred value support', 'Nutrition claims eligibility', 'Nutrition score estimation']
    }, {
        title: 'Technical and regulatory data',
        visual: 'technical-safety-data',
        description: 'Record physicochemical measurements, sensory attributes, technical effects and EU E-number information linked to each formulation.',
        bullets: ['Physicochemical measurements', 'Sensory and technical insights', 'EU E-number tracking']
    }, {
        title: 'Aminogram and protein quality',
        visual: 'aminogram',
        description: 'Review amino acid profiles and calculate protein quality scores such as PDCAAS and AAS from formulation data.',
        bullets: ['Amino acid profile', 'PDCAAS and AAS calculation', 'Limiting amino acid detection', 'Reference values from the World Health Organization']
    }],
    related: ['ingredient-library', 'flm']
}, {
    slug: 'development-assistant',
    id: 'development-assistant',
    icon: <AutoAwesome/>,
    overline: 'AI assistant',
    title: 'Ask product and regulatory questions in context',
    description: 'Use an AI assistant aware of platform data to answer formulation, ingredient, regulatory and workflow questions.',
    heroPlaceholder: 'Assistant conversation with sources and tools used',
    image: '/assets/assistant_gpt_new.png',
    benefits: [{
        title: 'In-workflow help', description: 'Get answers immediately without switching tabs or searching PDFs.'
    }, {
        title: 'Complex regulations', description: 'Navigate intricate global food laws with an AI trained on regulatory data.'
    }, {
        title: 'Faster discovery', description: 'Find the right ingredients or formulations by asking simple questions.'
    }],
    capabilities: [{
        icon: <AutoAwesome/>, title: 'Context-aware answers', description: 'The AI understands the specific product you are working on.'
    }, {
        icon: <VerifiedUser/>, title: 'Regulatory guidance', description: 'Support for navigating EU, US, and other regional food laws.'
    }, {
        icon: <Search/>, title: 'Source visibility', description: 'Always see the documents and data points used to generate answers.'
    }, {
        icon: <Timeline/>, title: 'Tool usage', description: 'The assistant uses platform tools to fetch real-time data.'
    }, {
        icon: <Dashboard/>, title: 'Navigation support', description: 'Help finding features and data within the CibusAI platform.'
    }, {
        icon: <Science/>, title: 'Data lookup', description: 'Query ingredients and formulations using natural language.'
    }],
    howItWorks: [{
        title: 'Ask a question', visual: 'assistant-ask-question', description: 'Query the assistant about a product, ingredient, or regulation.',
    }, {
        title: 'AI processes context', visual: 'assistant-process-context', description: 'The assistant analyzes platform data and available regulatory tools.',
    }, {
        title: 'Review and act', visual: 'assistant-review-act', description: 'Get a sourced answer and move forward with your next steps.',
    },],
    subfeatures: [{
        title: 'Regulatory Q&A',
        visual: 'regulatory-qa',
        description: 'Ask direct regulatory questions about ingredients, additives, use limits and labelling requirements across supported markets.',
        bullets: ['EU/FDA regulatory support', 'Additive and use-limit checks', 'Labelling requirement guidance'],
    }, {
        title: 'Platform help',
        visual: 'platform-help',
        description: 'Get contextual guidance while using CibusAI, with step-by-step walkthroughs for common tasks across formulation, nutrition, labelling and review workflows.',
        bullets: ['Step-by-step walkthroughs for key tasks', 'Context-aware guidance based on the current workflow', 'Practical help for formulation, labelling and validation steps']
    }, {
        title: 'Context-aware support',
        visual: 'context-aware-support',
        description: 'Receive assistant responses that take the active product, formulation data, ingredients and workflow context into account.',
        bullets: ['Active product and formulation context', 'Ingredient-aware analysis', 'Workflow-specific suggestions']
    }, {
        title: 'Sources and transparency',
        visual: 'sources-transparency',
        description: 'Review assistant answers with visible source references, linked platform data and confidence indicators so users can verify how each response was produced.',
        bullets: ['PDF and regulation citations', 'Direct links to platform data', 'Confidence and evidence indicators']
    }],
    related: ['formulation-designer', 'ingredient-library']
}, {
    slug: 'formulation-lifecycle-manager',
    id: 'flm',
    icon: <Assignment/>,
    overline: 'Lifecycle management',
    title: 'Manage products from idea to production',
    description: 'Track products, formulations and development stages across the full lifecycle, from early ideas to production and post-launch evaluation.',
    heroPlaceholder: 'Product lifecycle dashboard with formulation progress',
    image: '/assets/formulations_flm_new.png',
    benefits: [{
        title: 'Track progress', description: 'Always know the current status of every product in your pipeline.'
    }, {title: 'Streamline approvals', description: 'Manage review cycles and approvals within a single platform.'}, {
        title: 'Preserve history', description: 'Keep a traceable record of reviews, stage changes, archived formulations and product evolution.'
    }],
    capabilities: [{
        icon: <Timeline/>, title: 'Stage tracking', description: 'Define and monitor custom development stages.'
    }, {
        icon: <FactCheck/>, title: 'Approval loops', description: 'Built-in workflows for internal review and sign-off.'
    }, {
        icon: <History/>, title: 'Version history', description: 'Track every iteration of a formulation over time.'
    }, {
        icon: <CompareArrows/>, title: 'Reformulation heritage', description: 'See how a product has evolved from its predecessors.'
    }, {icon: <Storage/>, title: 'Archiving', description: 'Securely store historical data for future reference.'}, {
        icon: <Dashboard/>, title: 'Overview dashboards', description: 'High-level visibility for managers and directors.'
    }],
    howItWorks: [{
        title: 'Create or select', visual: 'create-select', description: 'Start a new product project or select an existing one.'
    }, {
        title: 'Track stages', visual: 'track-stages', description: 'Move formulations through idea, prototype, scale-up and production stages.'
    }, {
        title: 'Review and finalise', visual: 'review-finalize', description: 'Complete approvals, move products to production, or archive inactive formulations.'
    }],
    subfeatures: [{
        title: 'Stage-gate development',
        description: 'Structured workflows to ensure all criteria are met before moving forward.',
        bullets: ['Gate requirements', 'Status tracking'],
        imageCrop: '/assets/flm_stage_gate_croped.png',
        image: '/assets/flm_stage_gate_v2.png',
        visual: 'stage-gate'
    }, {
        title: 'Review queue',
        visual: 'review-loop',
        description: 'Manage pending and completed formulation reviews before products move to the next development stage.',
        bullets: ['Pending review visibility', 'Reviewer sign-off', 'Approval status tracking', 'Approved and rejected changes']
    }, {
        title: 'Review history',
        visual: 'review-history',
        description: 'Track the full timeline of review events, comments and stage-change decisions for each formulation.',
        bullets: ['Timeline of review events', 'Comment trail', 'State-change history', 'Decision traceability']
    }],
    related: ['formulation-designer', 'management-panel']
}, {
    slug: 'management-panel',
    id: 'management-panel',
    icon: <Dashboard/>,
    overline: 'Operational oversight',
    title: 'Monitor product development activity',
    description: 'Give administrators and managers a central view of products, ingredients, formulations and platform activity.',
    heroPlaceholder: 'Management dashboard with metrics and latest activity',
    image: '/assets/management_area.png',
    benefits: [{
        title: 'Instant visibility', description: 'Understand your team\'s activity and progress at a glance.'
    }, {
        title: 'Better tracking', description: 'Monitor counts of products, ingredients, and formulations in real-time.'
    }, {
        title: 'Support oversight', description: 'Ensure operational consistency across all development workflows.'
    }],
    capabilities: [{
        icon: <Dashboard/>, title: 'Management dashboard', description: 'Centralised view of key performance metrics.'
    }, {
        icon: <Timeline/>, title: 'Activity tracking', description: 'Detailed logs of product and formulation updates.'
    }, {
        icon: <Storage/>, title: 'Resource counts', description: 'Monitor the growth of your ingredient and recipe library.'
    }, {
        icon: <History/>, title: 'Recent activity', description: 'See what has been changed across the platform recently.'
    }, {icon: <Notifications/>, title: 'Alerts', description: 'Get notified of important events or pending actions.'}, {
        icon: <Groups/>, title: 'Team visibility', description: 'See how different teams are contributing to projects.'
    }],
    howItWorks: [{
        title: 'Review activity', visual: 'management-review-activity', description: 'Open the management panel to review recent platform-wide activity and pending updates.',
    }, {
        title: 'Track progress', visual: 'management-track-progress', description: 'Use project metrics to understand status, workload and workflow bottlenecks.',
    }, {
        title: 'Act on updates', visual: 'management-act-updates', description: 'Respond to alerts, reviews or pending actions to keep work moving.',
    },],
    subfeatures: [{
        title: 'Platform overview',
        visual: 'management-platform-overview',
        description: 'Monitor high-level platform activity across products, formulations, ingredients, users and documents from a central administration view.',
        bullets: ['Products and formulations count', 'Users, ingredients and documents overview', 'FLM stage metrics'],
    }, {
        title: 'Product and formulation tracking',
        visual: 'management-formulation-tracking',
        description: 'Track product creation, formulation activity and review states to understand progress across the development pipeline.',
        bullets: ['Product creation over time', 'Formulation review status', 'Stage transition visibility'],
    }, {
        title: 'Ingredient usage analytics',
        visual: 'management-ingredient-analytics',
        description: 'Analyse ingredient usage across the platform to identify frequently used, underused or strategically important ingredients.',
        bullets: ['Most used ingredients', 'Least used ingredients', 'Usage count analysis'],
    // }, {
    //     title: 'Documents and configuration',
    //     visual: 'management-docs-configuration',
    //     description: 'Configure the shared reference data that supports formulation, labelling and regulatory workflows, including nutritional parameters, annotation labels and supporting documents.',
    //     bullets: ['Custom nutritional parameter setup', 'Annotation and allergen label management', 'Supporting document administration'],
    }],
    related: ['flm', 'data-integration']
}, {
    slug: 'data-integration',
    id: 'data-integration',
    icon: <CloudUpload/>,
    overline: 'Data integration',
    title: 'Bring supplier and legacy data into CibusAI',
    description: 'Import, migrate and connect product, formulation and supplier data from spreadsheets, PDFs and enterprise systems such as SAP.',
    heroPlaceholder: 'Supplier document and spreadsheet import workflow',
    image: '/assets/import_ingredient.png',
    benefits: [{
        title: 'Reduce manual entry', description: 'Automate the ingestion of data from various document formats.'
    }, {title: 'Accelerate onboarding', description: 'Get new supplier data into the system in minutes, not days.'}, {
        title: 'Connect legacy data', description: 'Link your existing history with modern formulation tools.'
    }],
    capabilities: [{
        icon: <FileDownload/>, title: 'TDS import', description: 'Intelligent import of technical data sheets.'
    }, {
        icon: <Storage/>, title: 'Spreadsheet workflows', description: 'Bulk import tools for XLSX and CSV datasets.'
    }, {
        icon: <CloudUpload/>, title: 'Bulk import', description: 'Upload entire product or ingredient libraries at once.'
    }, {
        icon: <VerifiedUser/>, title: 'Structured records', description: 'Automatically map data to structured platform fields.'
    }, {icon: <History/>, title: 'Legacy migration', description: 'Support for migrating data from older systems.'}, {
        icon: <CompareArrows/>, title: 'ERP/SAP integration', description: 'Custom services to connect with your enterprise stack.'
    }],
    howItWorks: [{
        title: 'Upload source files', visual: 'data-upload-files', description: 'Upload supplier PDFs, technical data sheets or product formulation spreadsheets into the importer.',
    }, {
        title: 'Review and map data', visual: 'data-review-map', description: 'Review the extracted values, correct missing or ambiguous fields, and map the imported data to the right CibusAI structures.',
    }, {
        title: 'Use across CibusAI', visual: 'data-start-using', description: 'Validated data becomes available in ingredient libraries, product records and product formulation versions.',
    },],
    subfeatures: [{
        title: 'Supplier data and TDS importer',
        visual: 'supplier-tds-importer',
        description: 'AI-assisted extraction of supplier Technical Data Sheets and specification PDFs to centralise ingredient data in CibusAI.',
        bullets: ['Technical data sheet extraction', 'Nutritional data extraction', 'Allergen and attribute detection', 'Value and unit validation']
    }, {
        title: 'Bulk product and formulation importer',
        visual: 'bulk-product-formulation-importer',
        description: 'Import internal product portfolios and formulation spreadsheets, including ingredients, quantities and product metadata.',
        bullets: ['XLSX product imports', 'Formulation recipe imports', 'Ingredient quantity mapping', 'Error reporting'],
    }, {
        title: 'Enterprise and ERP integration',
        visual: 'enterprise-erp-integration',
        description: 'Support integration with enterprise systems such as SAP, helping migrate legacy data and connect R&D workflows with production data sources.',
        bullets: ['SAP and ERP integration support', 'Legacy data migration', 'Custom mapping rules', 'API and connector-based integration'],
    },],
    related: ['ingredient-library', 'management-panel'],
}];
